import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Target, 
  Eye,
  Users,
  Award,
  ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage, translations } from "../context/LanguageContext";

const valueIcons = [Heart, Target, Users, Award];

const AboutPage = () => {
  const { language } = useLanguage();
  const t = translations[language].about;

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1652404796149-b63ebbfe61aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwZmFtaWx5JTIwc3VwcG9ydCUyMGNvbW11bml0eSUyMEJsYWNrJTIwTGF0aW5vfGVufDB8fHx8MTc3NTY4NDE5MXww&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.heroOverline}
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              data-testid="about-hero-title"
            >
              {t.heroTitle}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600">
              {t.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-gray-100"
            >
              <div className="w-14 h-14 bg-warm-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-warm-700" />
              </div>
              <h2 
                className="text-2xl font-semibold text-gray-900 mb-4"
                style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                {t.missionTitle}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {t.missionText}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-gray-100"
            >
              <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-sky-700" />
              </div>
              <h2 
                className="text-2xl font-semibold text-gray-900 mb-4"
                style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                {t.visionTitle}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {t.visionText}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Our Work Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
                {t.leadershipOverline}
              </span>
              <h2 
                className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-2"
                style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                {t.founderName}
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-6 mt-6">
                {t.founderBio1}
              </p>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                {t.founderBio2}
              </p>

              <blockquote className="border-l-4 border-warm-700 pl-6 italic text-gray-700">
                "{t.founderQuote}"
              </blockquote>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.valuesOverline}
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.valuesTitle}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((value, idx) => {
              const ValueIcon = valueIcons[idx];
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white border border-gray-100 rounded-2xl p-6 text-center hover:shadow-md transition-all"
                  data-testid={`value-card-${idx}`}
                >
                  <div className="w-14 h-14 bg-warm-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ValueIcon className="w-7 h-7 text-warm-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.storyOverline}
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-8"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.storyTitle}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none text-gray-600"
          >
            {t.storyParagraphs.map((paragraph, idx) => (
              <p key={paragraph.substring(0, 50)} className={`leading-relaxed ${idx < t.storyParagraphs.length - 1 ? 'mb-6' : ''}`}>
                {paragraph}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-warm-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.ctaTitle}
            </h2>
            <p className="text-warm-100 text-lg mb-8 max-w-2xl mx-auto">
              {t.ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  data-testid="about-contact-btn"
                  className="bg-white text-warm-700 hover:bg-warm-50 rounded-full px-8 py-6"
                >
                  {t.ctaContact}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button 
                  data-testid="about-programs-btn"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6"
                >
                  {t.ctaPrograms}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
